import { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight, Calendar, Palette, Heart } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const API = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : '/api';

const BASE = process.env.REACT_APP_BACKEND_URL || '';
const resolveUrl = (url) => url?.startsWith('/api/uploads/') ? `${BASE}${url}` : url;

function CatCard({ cat, onClick }) {
  return (
    <div
      className="cat-card bg-white rounded-3xl overflow-hidden shadow-soft cursor-pointer"
      onClick={onClick}
      data-testid={`cat-card-${cat.name.toLowerCase()}`}
    >
      <div className="relative h-72 img-hover-zoom">
        <img
          src={resolveUrl(cat.images[0])}
          alt={cat.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full">
          <span className="font-body text-sm font-medium text-forest">{cat.role}</span>
        </div>
      </div>
      <div className="p-6">
        <h3 className="font-heading text-2xl font-bold text-forest mb-2">{cat.name}</h3>
        <p className="font-body text-autumn font-medium mb-3">{cat.character}</p>
        <div className="flex flex-wrap gap-2">
          <span className="flex items-center gap-1 text-xs bg-sand/50 text-forest/80 px-2 py-1 rounded-full">
            <Palette className="w-3 h-3" />
            {cat.color}
          </span>
          <span className="flex items-center gap-1 text-xs bg-sand/50 text-forest/80 px-2 py-1 rounded-full">
            <Calendar className="w-3 h-3" />
            {cat.birth_date}
          </span>
        </div>
      </div>
    </div>
  );
}

function CatModal({ cat, isOpen, onClose }) {
  const [currentImage, setCurrentImage] = useState(0);

  if (!cat) return null;

  const nextImage = () => {
    setCurrentImage((prev) => (prev + 1) % cat.images.length);
  };

  const prevImage = () => {
    setCurrentImage((prev) => (prev - 1 + cat.images.length) % cat.images.length);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl bg-cream border-sand p-0 overflow-hidden">
        <div className="grid md:grid-cols-2">
          {/* Image gallery */}
          <div className="relative h-80 md:h-full min-h-[400px] bg-forest/5">
            <img
              src={resolveUrl(cat.images[currentImage])}
              alt={`${cat.name} - photo ${currentImage + 1}`}
              className="w-full h-full object-cover"
            />
            {cat.images.length > 1 && (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white"
                  onClick={prevImage}
                  data-testid="prev-image-btn"
                >
                  <ChevronLeft className="w-5 h-5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white"
                  onClick={nextImage}
                  data-testid="next-image-btn"
                >
                  <ChevronRight className="w-5 h-5" />
                </Button>
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                  {cat.images.map((_, idx) => (
                    <button
                      key={idx}
                      className={`w-2 h-2 rounded-full transition-colors ${
                        idx === currentImage ? "bg-white" : "bg-white/50"
                      }`}
                      onClick={() => setCurrentImage(idx)}
                    />
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Info */}
          <div className="p-8">
            <DialogHeader>
              <DialogTitle className="font-heading text-3xl font-bold text-forest mb-2">
                {cat.name}
              </DialogTitle>
            </DialogHeader>
            
            <span className="inline-block bg-autumn/10 text-autumn font-body font-semibold px-3 py-1 rounded-full text-sm mb-4">
              {cat.role}
            </span>
            
            <p className="font-body text-forest/80 leading-relaxed mb-6">
              {cat.description}
            </p>

            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <Palette className="w-5 h-5 text-forest/60" />
                <div>
                  <span className="font-body text-xs text-forest/60">Couleur</span>
                  <p className="font-body font-medium text-forest">{cat.color}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-forest/60" />
                <div>
                  <span className="font-body text-xs text-forest/60">Date de naissance</span>
                  <p className="font-body font-medium text-forest">{cat.birth_date}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Heart className="w-5 h-5 text-forest/60" />
                <div>
                  <span className="font-body text-xs text-forest/60">Caractère</span>
                  <p className="font-body font-medium text-forest">{cat.character}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function CatsSection() {
  const [cats, setCats] = useState([]);
  const [selectedCat, setSelectedCat] = useState(null);

  useEffect(() => {
    const fetchCats = async () => {
      try {
        const response = await fetch(`${API}/cats`);
        if (response.ok) {
          const data = await response.json();
          setCats(data);
        }
      } catch (error) {
        console.error("Error fetching cats:", error);
      }
    };
    fetchCats();
  }, []);

  return (
    <section
      id="chats"
      data-testid="cats-section"
      className="relative py-24 lg:py-32 bg-cream-alt overflow-hidden"
    >
      {/* Decorative elements */}
      <div className="absolute top-20 left-10 text-6xl opacity-5">🐾</div>
      <div className="absolute bottom-20 right-10 text-6xl opacity-5">🐾</div>

      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Section header */}
        <div className="section-animate text-center mb-16">
          <span className="font-accent text-autumn text-xl">Nos pensionnaires</span>
          <h2 className="font-heading text-4xl sm:text-5xl font-bold text-forest mt-2 mb-6">
            Nos Chats Sibériens
          </h2>
          <p className="font-body text-lg text-forest/70 max-w-2xl mx-auto">
            Découvrez nos reproducteurs, soigneusement sélectionnés pour leurs
            lignées pures, leur beauté et leur caractère exceptionnel.
          </p>
        </div>

        {/* Cats grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {cats.map((cat, index) => (
            <div
              key={cat.name}
              className="section-animate"
              style={{ animationDelay: `${index * 0.15}s` }}
            >
              <CatCard cat={cat} onClick={() => setSelectedCat(cat)} />
            </div>
          ))}
        </div>

        {/* Note about future cats */}
        <div className="section-animate mt-12 text-center">
          <p className="font-body text-forest/60 text-sm italic">
            Cette section sera mise à jour avec de nouveaux reproducteurs au fil du temps.
          </p>
        </div>
      </div>

      {/* Modal */}
      <CatModal
        cat={selectedCat}
        isOpen={!!selectedCat}
        onClose={() => setSelectedCat(null)}
      />
    </section>
  );
}
